import React, { forwardRef, ReactElement } from 'react';
import {
  CommonInputHelperText,
  LabeledWrapper,
  LegendLabel,
  StyledTextArea,
} from './Forms.styled';
import { FontBasedSize } from '../../../styles/fontsStyleUtils';
import { FORM_FIELD_ERROR_ARIA_LIVE_VALUE } from './Forms';
import { RequiredLabel } from './RequiredLabel';

type GlobalTextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> &
  FontBasedSize & {
    label?: string;
    placeholder?: string;
    hasError?: boolean;
    helperText?: string;
    disabled?: boolean;
    required?: boolean;
  };

export default forwardRef<HTMLTextAreaElement, GlobalTextAreaProps>(
  function TextArea(
    {
      label,
      name,
      id,
      placeholder,
      hasError,
      helperText,
      disabled,
      required,
      fontBasedSize,
      ...rest
    },
    ref,
  ): ReactElement {
    const inputId = id || name;
    const helperTextId = `${inputId}-helper-text`;
    const showHelperText = Boolean(helperText) && !disabled;

    return (
      <LabeledWrapper>
        <StyledTextArea
          ref={ref}
          id={inputId}
          name={name}
          placeholder={placeholder}
          disabled={disabled}
          hasError={hasError}
          fontBasedSize={fontBasedSize}
          aria-describedby={showHelperText ? helperTextId : undefined}
          aria-invalid={hasError}
          {...rest}
        />
        {Boolean(label) && (
          <LegendLabel>
            <RequiredLabel label={label} required={required} />
          </LegendLabel>
        )}
        {showHelperText ? (
          <CommonInputHelperText
            hasError={hasError}
            disabled={disabled}
            id={helperTextId}
            aria-live={hasError ? FORM_FIELD_ERROR_ARIA_LIVE_VALUE : undefined}
          >
            {helperText}
          </CommonInputHelperText>
        ) : (
          <CommonInputHelperText>
            {/* Empty input helper text to keep minimal distance after textarea... */}
          </CommonInputHelperText>
        )}
      </LabeledWrapper>
    );
  },
);
